import React, { useId, useState } from 'react';
import { DatePicker, Select } from 'antd';
import dayjs from 'dayjs';

import { RANGE_PRESETS } from '../../utils/analytics';
import { RANGE_PRESET_LABELS, isRangeDay, rangeLabel } from '../../utils/chartRanges';

const SAVED = 'saved';
const CUSTOM = 'custom';

/**
 * The chart page's date range (plan §6.3): replaces the chart's saved range
 * for this visit only. The choice lives in the URL (`?range=last90d`, or
 * `?from=…&to=…` for custom dates), never in the chart, so a link carries it
 * and the chart stays as its author saved it. **As saved** puts the chart's
 * own range back.
 *
 * Days are the collection's (the server works the range out in its zone);
 * custom dates are calendar days, both included, 1900–2200.
 *
 *   saved     the chart's own `time.range` ({ preset } | { from, to } | null)
 *   value     the override: undefined (as saved), { preset }, or { from, to }
 *   onChange  (next | undefined) => void
 *
 * A dashboard's range (charts A6) is the same control with its first choice
 * renamed: **Each chart's own dates** (`savedLabel`), which is `null` there
 * (`savedValue`) — no range, so each chart keeps its own.
 */
export default function ChartRangeControl({ saved, value, onChange, phone = false, savedLabel, savedValue }) {
	const custom = Boolean(value && value.from);
	// "Custom dates…" picked, before any dates are: the pickers show, the chart keeps its range.
	const [picking, setPicking] = useState(false);
	const labelId = `chart-range-${useId().replace(/[^A-Za-z0-9_-]/g, '')}`;
	const atSaved = value === undefined || value === null;
	// "Custom dates…" picked shows the two days whatever is in force now — the
	// saved range or a preset — until a day range is chosen or another option
	// is (the A5 review's M-3: from a preset the pickers never opened).
	const selected = picking || custom ? CUSTOM : (atSaved ? SAVED : value.preset);

	const options = [
		{ value: SAVED, label: savedLabel || `As saved: ${rangeLabel(saved)}` },
		...RANGE_PRESETS.map((preset) => ({ value: preset, label: RANGE_PRESET_LABELS[preset] || preset })),
		{ value: CUSTOM, label: 'Custom dates…' },
	];

	const pick = (next) => {
		if (next === SAVED) {
			setPicking(false);
			onChange(savedValue);
		} else if (next === CUSTOM) {
			setPicking(true);
		} else {
			setPicking(false);
			onChange({ preset: next });
		}
	};

	const dates = custom ? [dayjs(value.from), dayjs(value.to)] : null;
	const minDay = dayjs('1900-01-01');
	const maxDay = dayjs('2200-12-31');

	return (
		<div className={`chart-range${phone ? ' chart-range--phone' : ''}`}>
			<span className='micro-label chart-range__label' id={labelId}>Date range</span>
			<Select
				className='chart-range__select'
				aria-labelledby={labelId}
				value={selected}
				options={options}
				onChange={pick}
				popupMatchSelectWidth={false}
			/>
			{selected === CUSTOM ? (
				<DatePicker.RangePicker
					className='chart-range__dates'
					aria-label='Custom dates'
					value={dates}
					allowClear={false}
					disabledDate={(day) => day.isBefore(minDay, 'day') || day.isAfter(maxDay, 'day')}
					onChange={(next) => {
						const from = next?.[0] ? next[0].format('YYYY-MM-DD') : null;
						const to = next?.[1] ? next[1].format('YYYY-MM-DD') : null;
						if (isRangeDay(from) && isRangeDay(to) && from <= to) {
							setPicking(false);
							onChange({ from, to });
						}
					}}
				/>
			) : null}
		</div>
	);
}
